"use client";
import { useState, useEffect, useRef } from 'react';
import usePlacesAutocomplete, {
  getGeocode,
  getDetails,
} from "use-places-autocomplete";
import { MapPinIcon } from "@heroicons/react/24/outline";
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxOption,
} from "@reach/combobox";
import { APIProvider } from '@vis.gl/react-google-maps';
import { GOOGLE_MAPS_API_KEY } from "data/constants";
import { useTranslation } from "react-i18next";


interface PlaceAutocompleteProps {
    onPlaceSelect: (place: PlaceResult | null) => void;
    placeholder: string;
}

export interface PlaceResult {
	formatted_address?: string;
	latitude?: number;
	longitude?: number;
	city?: string;
	province?: string;
	region?: string;
	country?: string;
	place_id?: string;
	postal_code?: string;
	street_number?: string;
	route?: string;
}
  
export const PlaceAutocomplete = ({ onPlaceSelect, placeholder}: PlaceAutocompleteProps) => {
  
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [showPopover, setShowPopover] = useState(false);
	const { t, ready } = useTranslation("translation");
	
	const { 
		value, 
		setValue, 
		suggestions: { 
			// loading,
			status, 
			data,
		}, 
		clearSuggestions, 
		init 
	} = usePlacesAutocomplete({
		requestOptions: {
			language: "it",
		},
		debounce: 300,
		cache: false,
		initOnMount: false,
	});

	const eventClickOutsideDiv = (event: MouseEvent) => {
		if (!containerRef.current) return;
		// CLICK IN_SIDE
		if (!showPopover || containerRef.current.contains(event.target as Node)) {
			return;
		}
		// CLICK OUT_SIDE
		setShowPopover(false);
		setTimeout(() => {
			clearSuggestions();
		}, 0);
	};
	

	useEffect(() => {
		if (eventClickOutsideDiv) {
		  document.removeEventListener("click", eventClickOutsideDiv);
		}
		showPopover && document.addEventListener("click", eventClickOutsideDiv);
		return () => {
		  document.removeEventListener("click", eventClickOutsideDiv);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPopover]);


	const handleSelectLocation = async (address: any) => {

		setValue(address, false);
		const results = await getGeocode({ address });
		const details = await getDetails({ placeId: results[0].place_id, language: "it" });
	
		if (typeof details === "string") return;
	
		const latitude = details?.geometry?.location?.lat();
		const longitude = details?.geometry?.location?.lng();

		let city, province, region, postal_code, country, street_number, route;
		// if in address_components.types is not included "locality" then select region or country
		if (details.address_components) {
			city = details?.address_components?.find((item: any) => item.types.includes("locality"));
			province = details?.address_components?.find((item: any) => item.types.includes("administrative_area_level_2"));
			region = details?.address_components.find((item: any) => item.types.includes("administrative_area_level_1"));
			country = details?.address_components.find((item: any) => item.types.includes("country"));
			postal_code = details?.address_components.find((item: any) => item.types.includes("postal_code"));
			street_number = details?.address_components.find((item: any) => item.types.includes("street_number"));
			route = details?.address_components.find((item: any) => item.types.includes("route"));
		}
	
		onPlaceSelect({
			latitude: latitude,
			longitude: longitude,
			formatted_address: details.formatted_address,
			city: city?.long_name || city?.short_name || "",
			region: region?.long_name || region?.short_name || "",
			province: province?.long_name || province?.short_name || "",
			country: country?.long_name || country?.short_name || "",
			postal_code: postal_code?.long_name || postal_code?.short_name || "",
			place_id: details.place_id || "",
			street_number: street_number?.long_name || street_number?.short_name || "",
			route: route?.long_name || route?.short_name || "",
		})
	
		setShowPopover(false);
	};




	const renderSearchValue = () => {

		return (
		  <>
		  	{
				status === "OK" && data.length === 0 && (
					<div className="text-sm text-neutral-500 dark:text-neutral-400 px-4 sm:px-8">
					{t("translation:search.pages.fallback.noResults")}
					</div>
				)
			}
			{
				(status !== "OK" && status.length > 0) && (
					<div className="text-sm text-neutral-500 dark:text-neutral-400 px-4 sm:px-8">
					{`Error: ${status}`}
					</div>
				)
			}
			{/* {
				loading && (
					<div className="text-sm text-neutral-500 dark:text-neutral-400 px-4 sm:px-8">
					{t("translation:search.pages.fallback.loadingResults")}
					</div>
				)
			} */}
			{data.map(({ place_id, description }) => (
			  <span
				onClick={handleSelectLocation.bind(null, description)}
				key={place_id}
				className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
			  >
				<span className="block text-neutral-400">
				  <MapPinIcon className="h-4 w-4 sm:h-6 sm:w-6" />
				</span>
				<span className="block font-medium text-neutral-700 dark:text-neutral-200">
				  <ComboboxOption key={place_id} value={description} style={{listStyle: "none"}} />
				</span>
			  </span>
			))}
		  </>
		);
	};

	if (!ready) {
		return <div>Loading...</div>;
	}

  
    return (
      <div className="autocomplete-container">
		<APIProvider
			apiKey={GOOGLE_MAPS_API_KEY}
			libraries={['places', 'geocoding']}
			authReferrerPolicy="origin"
			onError={(e) => console.error("Error loading Google Maps API", e)}
			onLoad={() => {
				console.log("Google Maps API loaded successfully");
				init();
			}}
		>
			<Combobox onSelect={handleSelectLocation}>
				<div className={`relative flex`} ref={containerRef}>
					<ComboboxInput
						value={value}
						type="text"
						onChange={(e) => {
							setShowPopover(true);
							setValue(e.target.value);
						}}
						className={`border-solid border-1 block w-full border-neutral-200 h-11 px-4 py-3 text-[1.0004rem] font-normal rounded-2xl focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 dark:bg-neutral-900`}
						placeholder={placeholder}
						ref={inputRef}
					/>
					<ComboboxPopover>

						{showPopover && value && (
							<div className="absolute left-0 z-40 w-full min-w-[300px] sm:min-w-[500px] bg-white dark:bg-neutral-800 top-full mt-3 py-3 sm:py-6 rounded-3xl shadow-xl max-h-96 overflow-y-auto">
								{renderSearchValue()}
							</div>
						)}
					</ComboboxPopover>
				</div>
			</Combobox>
		</APIProvider>
      </div>
    );
};
